const ELLIPSIS = '...' as const

type PaginationItem = number | typeof ELLIPSIS

interface GetPaginationRangeParams {
	currentPage: number
	lastPage: number
	siblingCount?: number
}

/**
 * Генерирует массив страниц для пагинации с truncation
 * Пример: [1, '...', 4, 5, 6, '...', 10]
 */
export const getPaginationRange = ({
	currentPage,
	lastPage,
	siblingCount = 1
}: GetPaginationRangeParams): PaginationItem[] => {
	// Минимум: первая + последняя + текущая + 2 многоточия + siblings с обеих сторон
	const totalPageNumbers = siblingCount * 2 + 5

	// Если страниц меньше чем нужно для truncation — показываем все
	if (totalPageNumbers >= lastPage) {
		return Array.from({ length: lastPage }, (_, i) => i + 1)
	}

	const leftSiblingIndex = Math.max(currentPage - siblingCount, 1)
	const rightSiblingIndex = Math.min(currentPage + siblingCount, lastPage)

	const shouldShowLeftEllipsis = leftSiblingIndex > 2
	const shouldShowRightEllipsis = rightSiblingIndex < lastPage - 1

	// Кейс 1: Только правое многоточие (текущая страница близко к началу)
	if (!shouldShowLeftEllipsis && shouldShowRightEllipsis) {
		const leftItemCount = 3 + 2 * siblingCount
		const leftRange = Array.from({ length: leftItemCount }, (_, i) => i + 1)

		return [...leftRange, ELLIPSIS, lastPage]
	}

	// Кейс 2: Только левое многоточие (текущая страница близко к концу)
	if (shouldShowLeftEllipsis && !shouldShowRightEllipsis) {
		const rightItemCount = 3 + 2 * siblingCount
		const rightRange = Array.from({ length: rightItemCount }, (_, i) => lastPage - rightItemCount + i + 1)

		return [1, ELLIPSIS, ...rightRange]
	}

	// Кейс 3: Оба многоточия (текущая страница посередине)
	const middleRange = Array.from({ length: rightSiblingIndex - leftSiblingIndex + 1 }, (_, i) => leftSiblingIndex + i)

	return [1, ELLIPSIS, ...middleRange, ELLIPSIS, lastPage]
}

export { ELLIPSIS }
export type { PaginationItem }
